import { app } from '@/bootloader';
import { computed, inject } from 'vue';
import {
  Builds_BuildResult,
  Builds_InitiatorType,
  Dashboard_ActiveHelperMachines,
  Dashboard_ActiveInitiatorMachines,
  Dashboard_CacheCoresUsage,
  Dashboard_HelperNeededRate,
  Dashboard_TimeSavedByCache,
  Dashboard_TotalWorkloads,
  Dashboard_WorkloadDuration,
  useCommonDataLazyQuery,
  useDashboardMetadataQuery,
} from '@/graphql/codegen/generated/graphql';
import { IncrediChartHeatmap, useIncrediDialog } from 'incredi-ui';
import { convertBaseTimeToSSMMHH, convertChartUnitsFromSeconds, getAllDatesInRange } from '@/views/dashboard/dashboard.charts.utils';
import moment from 'moment';
import { useLazyQueryWithCoordinator } from '@/services/graphql.utils';
import { ArgumentTypes } from '@/types/utils';
import { useRouter } from 'vue-router';
import { Routes } from '@/router/router.const';
import { IKWorkloadsPageData } from '@/views/workloads/workloads.injectionKeys';
import { IKWorkloadsPersistentData } from '@/views/workloads/workloads-table.types';
import { IKDashboard } from './dashboard.injectionKeys';
import { useDashboardFilters, useDashboardFiltersProvider } from './components/dashboard-filter-bar/dashboard-filter.provider';
import { dashboardDataGQL } from './dashboard.composable';
import { dayMap, hoursIndexMap, indexDayMap, targetBaseTimeMap } from './dashboard.const';
import { CLOUD_STATUS } from '../settings/settingsCloud/cloud.const';

const hiddenByFilterColor = 'rgba(0,0,0,0)';

const normalizeWorkloadDataAndGetMax = (workloadData: any) => {
  // Map the days of the week to numbers

  const result = [];
  let isEmpty = true;
  let maxDuration = 0; // Variable to track the maximum duration

  // Iterate over the object
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const day in workloadData) {
    const dayNumber = dayMap[day as keyof typeof dayMap]; // Get the numeric value for the day
    const hoursArray = workloadData[day];
    if (Array.isArray(hoursArray)) {
      for (let i = 0; i < hoursArray.length; i++) {
        const hourData = hoursArray[i];

        if (isEmpty && hourData.duration > 0) isEmpty = false;
        // Create a sub-array: [dayNumber, hour, duration]
        const normalizedData = {
          value: [i, dayNumber, hourData.duration],
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 2,
            ...(!hourData.duration ? { color: '#ECEDEF' } : {}),
          } as any,
        };
        result.unshift(normalizedData); // Add to the result array

        // Check for maximum duration
        if (hourData.duration > maxDuration) {
          maxDuration = hourData.duration;
        }
      }
    }
  }
  return {
    isEmpty,
    normalizedArray: result,
    maxDuration,
  };
};

const dashboardGridUtilization = () => {
  const gridUtilizationQuery = dashboardDataGQL('useSystemUtilizationQuery');
  const GQL_commonData = useLazyQueryWithCoordinator(useCommonDataLazyQuery);

  const helperNeedRateChart = computed(() => {
    const seriesMap: Record<Extract<keyof Dashboard_HelperNeededRate, 'maxCores' | 'demand' | 'availability'>, number> = {
      maxCores: 0,
      demand: 1,
      availability: 2,
    };
    const helperNeedRateChartNormalized = gridUtilizationQuery.result.value?.systemUtilization?.helperNeededRate?.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        Object.keys(rest).forEach(key => {
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            barMaxWidth: 50,
            name: `Assigned`,
            type: 'line',
            sampling: 'lttb',
            showSymbol: false,
            areaStyle: {},
            lineStyle: {
              opacity: 0,
            },
            itemStyle: {
              color: `#35CCD059`,
            },
            data: [],
            cursor: 'default',
          },
          {
            barMaxWidth: 50,
            name: `Requested`,
            type: 'line',
            sampling: 'lttb',
            showSymbol: false,
            itemStyle: {
              color: `#299DCF`,
            },
            data: [],
            cursor: 'default',
          },
          {
            barMaxWidth: 50,
            name: `Available`,
            showSymbol: false,
            type: 'line',
            sampling: 'lttb',
            itemStyle: {
              color: `#878C9C`,
            },
            data: [],
            cursor: 'default',
          },
        ],
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };
    const firstLabelIndexMap = helperNeedRateChartNormalized.labels.reduce((acc, label, index) => {
      if (index === 0) return acc;
      if (acc[label] === undefined)
        return {
          ...acc,
          [label]: index,
        };
      return acc;
    }, {} as Record<string, number>);
    return {
      chartId: 'helperNeededRate',
      title: 'Cores',
      options: {
        xAxis: {
          data: helperNeedRateChartNormalized.labels.map((label, index) =>
            JSON.stringify({
              label,
              index,
            }),
          ),

          axisLabel: {
            interval() {
              return true;
            },
            formatter(value: any) {
              const parsed = JSON.parse(value);
              const isFirstIndexOfLabel = firstLabelIndexMap[parsed.label] === parsed.index;
              return isFirstIndexOfLabel ? parsed.label : ' ';
            },
          },
        },
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF',
          },
          formatter(params: any) {
            const helperNeededRate = [...(gridUtilizationQuery.result.value?.systemUtilization?.helperNeededRate || [])].reverse();
            const item = helperNeededRate[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');
            const time = moment(item?.date).format('HH:mm');
            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="w-[160px]">
            <div class="ml-auto mb-[8px]">

            <span class="ml-auto font-gordita-medium text-[12px] mb-[8px]">${formattedDate} ${time}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                ${param.marker}
                <span>${param.seriesName}</span>
                <span class="ml-auto">${param.data.toLocaleString()}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...helperNeedRateChartNormalized,
    };
  });
  const buildCacheCpuRunTimeAndSavedTimeChart = computed(() => {
    const data = (gridUtilizationQuery.result.value?.systemUtilization?.timeSavedByCache || []) as Dashboard_TimeSavedByCache[];
    const computedData = convertChartUnitsFromSeconds(data, ['totalCpuTime', 'totalSavedTime']);
    const seriesMap: Record<Extract<keyof Dashboard_TimeSavedByCache, 'totalCpuTime' | 'totalSavedTime'>, number> = {
      totalCpuTime: 0,
      totalSavedTime: 1,
    };
    const buildCacheCpuRunTimeAndSavedTimeChartNormalized = computedData.convertedData?.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        Object.keys(rest).forEach(key => {
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            barMaxWidth: 50,
            name: `CPU run time`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            showSymbol: false,
            itemStyle: {
              color: `#30BABD`,
            },
            data: [],
            cursor: 'default',
          },
          {
            barMaxWidth: 50,
            name: `CPU saved time by Build Cache`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#F5B427`,
            },
            data: [],
            cursor: 'default',
          },
        ],
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };

    return {
      chartId: 'buildCacheCpuRunTimeAndSavedTimeChart',
      title: targetBaseTimeMap[computedData.unit],
      options: {
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF',
          },
          formatter(params: any) {
            const timeSavedByCache = [...(gridUtilizationQuery.result.value?.systemUtilization?.timeSavedByCache || [])].reverse();
            const item = timeSavedByCache[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');

            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="min-w-[160px]">
            <div class="ml-auto mb-[8px]">
            <span class="font-gordita-medium text-[12px]">${formattedDate}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                <span style=\\"display:inline-block;margin-right:4px;border-radius:3px;margin-bottom:2px;width:12px;height:12px;background-color:${param.color};\\"></span>
                <span class="mr-[5px]">${param.seriesName}</span>
                <span class="ml-auto">${convertBaseTimeToSSMMHH(param.data, computedData.unit)}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...buildCacheCpuRunTimeAndSavedTimeChartNormalized,
    };
  });
  const activeInitiatorMachines = computed(() => {
    const seriesMap: Record<Extract<keyof Dashboard_ActiveInitiatorMachines, 'maxCores' | 'demand' | 'availability'>, number> = {
      withInitiatorLicense: 0,
      withoutInitiatorLicense: 1,
    };
    const activeInitiatorMachinesNormalized = gridUtilizationQuery.result.value?.systemUtilization?.activeInitiatorMachines?.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        Object.keys(rest).forEach(key => {
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            barMaxWidth: 50,
            name: `Initiators`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            showSymbol: false,
            itemStyle: {
              color: `#35CCD0`,
            },
            data: [],
            cursor: 'default',
          },
          {
            barMaxWidth: 50,
            name: `Missing Initiator role`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#878C9C`,
            },
            data: [],
            cursor: 'default',
          },
        ],
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };

    return {
      chartId: 'activeInitiatorMachines',
      title: 'Initiators',
      options: {
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF',
          },
          formatter(params: any) {
            const gqlResult = [...(gridUtilizationQuery.result.value?.systemUtilization?.activeInitiatorMachines || [])].reverse();
            const item = gqlResult[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');

            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="w-[160px]">
            <div class="ml-auto mb-[8px]">

            <span class="ml-auto font-gordita-medium text-[12px] mb-[8px]">${formattedDate}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                <span style=\\"display:inline-block;margin-right:4px;border-radius:3px;margin-bottom:2px;width:12px;height:12px;background-color:${param.color};\\"></span>
                <span>${param.seriesName}</span>
                <span class="ml-auto">${param.data.toLocaleString()}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...activeInitiatorMachinesNormalized,
    };
  });
  const activeHelperMachines = computed(() => {
    const isCloudDataEnabled =
      GQL_commonData.result.value?.ibManagerEntities?.settings?.common?.cloud?.cloudStatus === CLOUD_STATUS.Enabled ||
      gridUtilizationQuery.result.value?.systemUtilization?.activeHelperMachines?.some(e => e?.cloud);
    const seriesMap: Record<Extract<keyof Dashboard_ActiveHelperMachines, 'cloud' | 'onPrem'>, number> = {
      onPrem: 0,
      cloud: 1,
    };
    const activeHelperMachinesNormalized = gridUtilizationQuery.result.value?.systemUtilization?.activeHelperMachines?.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        (Object.keys(rest) as Array<keyof typeof rest>).forEach(key => {
          if (key === 'cloud' && !isCloudDataEnabled) return;
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            barMaxWidth: 50,
            name: `On prem`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#35CCD0`,
            },
            data: [],
            cursor: 'default',
          },
          isCloudDataEnabled
            ? {
                barMaxWidth: 50,
                name: `Cloud`,
                type: 'bar',
                stack: 'total',
                sampling: 'lttb',
                showSymbol: false,
                itemStyle: {
                  color: `#9D55FB`,
                },
                data: [],
                cursor: 'default',
              }
            : undefined,
        ].filter(Boolean),
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };

    return {
      chartId: 'activeHelperMachines',
      title: 'Helpers',
      options: {
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF',
          },
          formatter(params: any) {
            const gqlResult = [...(gridUtilizationQuery.result.value?.systemUtilization?.activeHelperMachines || [])].reverse();
            const item = gqlResult[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');

            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="w-[160px]">
            <div class="ml-auto mb-[8px]">

            <span class="ml-auto font-gordita-medium text-[12px] mb-[8px]">${formattedDate}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                <span style=\\"display:inline-block;margin-right:4px;border-radius:3px;margin-bottom:2px;width:12px;height:12px;background-color:${param.color};\\"></span>
                <span>${param.seriesName}</span>
                <span class="ml-auto">${param.data.toLocaleString()}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...activeHelperMachinesNormalized,
    };
  });
  const buildCacheCoreUsageChart = computed(() => {
    const seriesMap: Record<Extract<keyof Dashboard_CacheCoresUsage, 'cacheCores'>, number> = {
      cacheCores: 0,
    };
    const buildCacheCoreUsageNormalized = gridUtilizationQuery.result.value?.systemUtilization?.cacheCoresUsage?.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        Object.keys(rest).forEach(key => {
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            barMaxWidth: 50,
            name: `Cache cores`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#35CCD0`,
            },
            data: [],
            cursor: 'default',
          },
        ],
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };

    const firstLabelIndexMap = buildCacheCoreUsageNormalized.labels.reduce((acc, label, index) => {
      if (index === 0) return acc;
      if (acc[label] === undefined)
        return {
          ...acc,
          [label]: index,
        };
      return acc;
    }, {} as Record<string, number>);
    return {
      chartId: 'Cache Cores',
      title: 'Cache Cores',
      options: {
        xAxis: {
          data: buildCacheCoreUsageNormalized.labels.map((label, index) =>
            JSON.stringify({
              label,
              index,
            }),
          ),
          axisLabel: {
            interval(_index: number, value: string) {
              const parsed = JSON.parse(value);
              return firstLabelIndexMap[parsed.label] === parsed.index;
            },
            formatter(value: any) {
              const parsed = JSON.parse(value);
              const isFirstIndexOfLabel = firstLabelIndexMap[parsed.label] === parsed.index;
              return isFirstIndexOfLabel ? parsed.label : '';
            },
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF',
          },
          formatter(params: any) {
            const helperNeededRate = [...(gridUtilizationQuery.result.value?.systemUtilization?.cacheCoresUsage || [])].reverse();
            const item = helperNeededRate[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');
            const time = moment(item?.date).format('HH:mm');

            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="w-[160px]">
            <div class="ml-auto mb-[8px]">
            <span class="ml-auto font-gordita-medium text-[12px] mb-[8px]">${formattedDate} ${time}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                <span style=\\"display:inline-block;margin-right:4px;border-radius:3px;margin-bottom:2px;width:12px;height:12px;background-color:${param.color};\\"></span>
                <span>${param.seriesName}</span>
                <span class="ml-auto">${param.data.toLocaleString()}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...buildCacheCoreUsageNormalized,
    };
  });
  return {
    gql: {
      gridUtilizationQuery,
    },
    charts: {
      activeHelperMachines,
      activeInitiatorMachines,
      buildCacheCoreUsageChart,
      helperNeedRateChart,
      buildCacheCpuRunTimeAndSavedTimeChart,
    },
  };
};

export const useFilteredDashboardMetadata = (options?: ArgumentTypes<typeof useDashboardMetadataQuery>['1']) => {
  const dashboardFilters = useDashboardFilters();
  return useDashboardMetadataQuery(
    () => ({
      filter: {
        from: dashboardFilters.rangeDate.value.from.valueOf(),
        to: dashboardFilters.rangeDate.value.to.valueOf(),
      },
    }),
    options,
  );
};

export const useDashboardProvider = () => {
  const router = useRouter();
  const workloadsData = inject(IKWorkloadsPageData);
  const workloadsPersistentData = inject(IKWorkloadsPersistentData);
  const { rangeDate, buildGroup, workloadType, initiatorType } = useDashboardFiltersProvider();
  const gridUtilization = dashboardGridUtilization();
  const workloadsQuery = dashboardDataGQL('useWorkloadsQuery');
  const dialog = useIncrediDialog();

  const workloadDurationByHoursChart = computed<
    InstanceType<typeof IncrediChartHeatmap>['$props'] & {
      isEmpty: boolean;
    }
  >(() => {
    const normalizeWorkload = normalizeWorkloadDataAndGetMax(workloadsQuery.result.value?.workloads?.workloadsHoursDuration);
    let selectedMinDuration = 0;
    let selectedMaxDuration = normalizeWorkload.maxDuration;
    return {
      onVisualMapChange: (params: { selected: Array<number> }) => {
        [selectedMinDuration, selectedMaxDuration] = params.selected;
      },
      onCellClick: (params: any) => {
        if (params.color === hiddenByFilterColor) {
          return;
        }
        const [h, d, duration] = params.value;
        if (!duration) {
          return;
        }
        const day = indexDayMap[d] as 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
        const hour = hoursIndexMap[h];
        const nextHour = hoursIndexMap[h + 1] || hoursIndexMap[0];

        workloadsData?.filter.clear(true);
        const allDates = getAllDatesInRange(rangeDate.value.from.valueOf(), rangeDate.value.to.valueOf(), day, hour);
        workloadsData?.filter.setTimeFrame(
          allDates.map(({ from, to }) => ({
            from: from.unix() * 1000,
            to: to.unix() * 1000,
          })),
          true,
        );

        workloadsPersistentData?.setCustomFilter(`${day}s ${hour} - ${nextHour}`);
        if (initiatorType.value) workloadsData?.filter?.setInitiatorType([initiatorType.value] as unknown as Builds_InitiatorType[]);
        if (buildGroup.value) workloadsData?.filter?.setBuildGroup([buildGroup.value]);
        if (workloadType.value) {
          workloadsData?.filter.setBuildType([workloadType.value as any]);
        }

        dialog?.close(`${workloadDurationByHoursChart.value.chartId}-dialog`);
        router.push({
          name: Routes.Builds,
        });
      },
      isEmpty: normalizeWorkload.isEmpty,
      chartId: 'workloadDurationByHoursChart',
      options: {
        visualMap: {
          max: normalizeWorkload.maxDuration,
          type: 'continuous',
          hoverLink: false,
          formatter(value: any) {
            return convertBaseTimeToSSMMHH(Math.floor(Number(value)));
          },
          text: ['', ''], // Custom text for the two ends
          textStyle: {
            color: '#7F8789',
          },
        },
        tooltip: {
          backgroundColor: '#2E3138',
          color: '#fff',
          formatter(params: any) {
            const [h, d, duration] = params.value;
            if (duration < selectedMinDuration || duration > selectedMaxDuration) {
              return '';
            }
            const day = indexDayMap[d] as 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
            const hour = hoursIndexMap[h];
            const nextHour = hoursIndexMap[h + 1] || hoursIndexMap[0];
            const buildNumAvg = workloadsQuery.result.value?.workloads?.workloadsHoursDuration?.[day]?.[h]?.totalWorkloads;
            // value[1];
            // Assuming the third item is the value and additional data is in the fourth
            return `<div style="color: white" class="min-w-[160px]">
                <div class="font-gordita-medium text-[12px] mb-[8px]">
                  <span>${day}</span>
                  <span>${hour} - ${nextHour}</span>
                </div>
                <div class="font-gordita-medium text-[11px] flex justify-between">
                  <span>Total run time</span>
                  <span>${convertBaseTimeToSSMMHH(duration)}</span>
                </div>
                <div class="font-gordita-regular text-[11px] flex justify-between">
                  <span>Builds</span>
                  <span>${buildNumAvg}</span>
                </div>
              </div>`;
          },
        },
      },
      xLabels: Object.values(hoursIndexMap),
      yLabels: ['Sun', 'Sat', 'Fri', 'Thu', 'Wed', 'Tue', 'Mon'],
      series: [
        {
          name: 'Punch Card',
          type: 'heatmap',
          data: normalizeWorkload.normalizedArray,
          label: {
            show: false,
            formatter(params: any) {
              // params.value[2] typically contains the data value in heatmap
              if (params.value[2] === 0) {
                return '';
              }
              return params.value[2];
            },
          },
          itemStyle: {
            normal: {
              color: '#ECEDEF', // this will be color of uncontrolled item
              borderColor: '#fff', // Use your background color
              borderWidth: 2, // Adjust the width to control the gap size
            },
          } as any,
        },
      ],
    };
  });

  const buildDurationChart = computed(() => {
    const data = (workloadsQuery.result.value?.workloads?.workloadsDuration || []) as Dashboard_WorkloadDuration[];
    const computedData = convertChartUnitsFromSeconds(data, ['avgWorkloadDuration', 'maxWorkloadDuration']);

    const seriesMap: Record<Extract<keyof Dashboard_WorkloadDuration, 'avgWorkloadDuration' | 'maxWorkloadDuration'>, number> = {
      maxWorkloadDuration: 0,
      avgWorkloadDuration: 1,
    };
    const buildDurationNormalized = computedData.convertedData.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        Object.keys(rest).forEach(key => {
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            name: `Max`,
            type: 'line',
            sampling: 'lttb',
            data: [],
            showSymbol: true, // Make sure symbols (dots) are shown
            symbolSize: 6, // You can adjust the size of the dots
            lineStyle: {
              width: 0, // Set line width to 0 to hide the line
            },
            itemStyle: {
              color: '#35CCD0', // Fill color for the dots
            },
            cursor: 'default',
          },
          {
            name: `Avg`,
            type: 'line',
            showSymbol: false, // Make sure symbols (dots) are shown

            sampling: 'lttb',
            itemStyle: {
              color: `#878C9C`,
            },
            data: [],
            cursor: 'default',
          },
        ],
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };

    return {
      chartId: 'buildDurationNormalized',
      title: targetBaseTimeMap[computedData.unit],
      options: {
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF', // Set the text color here
            // You can also add other styling properties like fontSize, fontFamily, etc.
          },
          formatter(params: any) {
            const workloadsDuration = [...(workloadsQuery.result.value?.workloads?.workloadsDuration || [])].reverse();
            const item = workloadsDuration[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');

            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="w-[160px]">
            <div class="ml-auto mb-[8px]">
            <span class="font-gordita-medium text-[12px]">${formattedDate}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                ${param.marker}
                <span> ${param.seriesName}</span>
                <span class="ml-auto">${convertBaseTimeToSSMMHH(param.data, computedData.unit)}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...buildDurationNormalized,
    };
  });
  const buildDurationPercentileChart = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...percentile } = workloadsQuery.result.value?.workloads?.workloadsDurationPercentile ?? {};
    return Object.entries(percentile)
      .map(([key, value]) => ({ key: key.toUpperCase(), value: convertBaseTimeToSSMMHH(value || 0) }))
      .sort((a, b) => +a.key - +b.key);
  });

  const totalWorkloadsChart = computed(() => {
    const seriesMap: Record<Extract<keyof Dashboard_TotalWorkloads, 'successful' | 'failed' | 'canceled'>, number> = {
      successful: 0,
      failed: 1,
      canceled: 2,
    };
    let filterMap: Record<string, boolean> = {};
    const totalWorkloadsChartNormalized = (workloadsQuery.result.value?.workloads?.totalWorkloads || [])?.reduce(
      (acc, item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, __typename, ...rest } = item as typeof item & { date: string };
        const dateLabel = moment(date).format('D MMM');
        acc.labels.unshift(dateLabel);

        Object.keys(rest).forEach(key => {
          acc.series[seriesMap[key as keyof typeof seriesMap]]?.data?.unshift(rest[key as keyof typeof rest]);
        });

        return acc;
      },
      {
        labels: [],
        series: [
          {
            barMaxWidth: 50,
            name: `Succeeded`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#35CCD0`,
            },
            data: [],
          },
          {
            barMaxWidth: 50,
            name: `Failed`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#FE8469`,
            },
            data: [],
          },
          {
            barMaxWidth: 50,
            name: `Cancelled`,
            type: 'bar',
            stack: 'total',
            sampling: 'lttb',
            itemStyle: {
              color: `#878C9C`,
            },
            data: [],
          },
        ],
      } as {
        labels: string[];
        series: any[];
      },
    ) || {
      labels: [],
      series: [],
    };

    return {
      chartId: 'totalWorkloads',
      title: 'Builds',
      onLegendSelectChanged: (params: any) => {
        filterMap = params.selected;
      },
      onCellClick: (params: any) => {
        const map = {
          Cancelled: Builds_BuildResult.Canceled,
          Failed: Builds_BuildResult.Failed,
          Successful: Builds_BuildResult.Success,
        };
        const workloadsDuration = [...(workloadsQuery.result.value?.workloads?.totalWorkloads || [])].reverse();
        const item = workloadsDuration[params?.dataIndex];
        const from = moment(item?.date);
        const to = moment(item?.date).add(1, 'day').subtract(1, 'millisecond');
        workloadsData?.filter.clear();
        workloadsData?.filter.setTimeFrame([{ from: from.toDate().getTime(), to: to.toDate().getTime() }]);
        if (Object.values(filterMap).some(value => !value)) {
          const filterArray: Builds_BuildResult[] = Object.keys(filterMap)
            .filter(key => filterMap[key])
            .map(key => (map as any)[key]);
          workloadsData?.filter.setBuildResult(filterArray);
        }
        workloadsPersistentData?.setCustomFilter();
        if (initiatorType.value) workloadsData?.filter?.setInitiatorType([initiatorType.value] as unknown as Builds_InitiatorType[]);
        if (buildGroup.value) workloadsData?.filter?.setBuildGroup([buildGroup.value]);
        if (workloadType.value) {
          workloadsData?.filter.setBuildType([workloadType.value as any]);
        }
        dialog?.close(`${totalWorkloadsChart.value.chartId}-dialog`);
        router.push({
          name: Routes.Builds,
        });
      },
      options: {
        tooltip: {
          show: true,
          backgroundColor: '#2E3138',
          textStyle: {
            color: '#FFF',
          },
          formatter(params: any) {
            const workloadsDuration = [...(workloadsQuery.result.value?.workloads?.totalWorkloads || [])].reverse();
            const item = workloadsDuration[params[0]?.dataIndex];
            const formattedDate = moment(item?.date).format('D MMM YYYY');

            // Custom formatting logic here
            // 'params' contains information about the data point being hovered
            return `<div style="color: white" class="w-[160px]">
            <div class="ml-auto mb-[8px]">

            <span class="ml-auto font-gordita-medium text-[12px] mb-[8px]">${formattedDate}</span>
            </div>
            ${params
              .map(
                (param: any) => `<div class="w-full flex items-center text-[11px]">
                <span style=\\"display:inline-block;margin-right:4px;border-radius:3px;margin-bottom:2px;width:12px;height:12px;background-color:${param.color};\\"></span>
                <span>${param.seriesName}</span>
                <span class="ml-auto">${param.data.toLocaleString()}</span>
              </div>`,
              )
              .join('')}
            </div>`;
          },
        },
      },
      ...totalWorkloadsChartNormalized,
    };
  });
  const metadata = computed(() => {
    const workloadsNoData = {
      buildDurationChart: buildDurationChart.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
      totalWorkloadsChart: totalWorkloadsChart.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
      workloadDurationByHoursChart: workloadDurationByHoursChart.value.isEmpty,
    };
    const gridUtilizationNoData = {
      helperNeedRateChart: gridUtilization.charts.helperNeedRateChart.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
      activeInitiatorMachines: gridUtilization.charts.activeInitiatorMachines.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
      activeHelperMachines: gridUtilization.charts.activeHelperMachines.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
      buildCacheCoreUsageChart: gridUtilization.charts.buildCacheCoreUsageChart.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
      buildCacheCpuRunTimeAndSavedTimeChart: gridUtilization.charts.buildCacheCpuRunTimeAndSavedTimeChart.value.series.every(s => !s.data.length || s.data.every((value: number) => !value)),
    };

    return {
      workloads: {
        noData: workloadsNoData,
      },
      gridUtilization: {
        noData: gridUtilizationNoData,
      },
    };
  });
  const payload = {
    workloads: {
      workloadDurationByHoursChart,
      buildDurationChart,
      buildDurationPercentileChart,
      totalWorkloadsChart,
      workloadsQuery,
    },
    gridUtilization: gridUtilization.charts,
    gql: {
      workloadsQuery,
      ...gridUtilization.gql,
    },
    metadata,
  };
  app.provide(IKDashboard, payload);
  return payload;
};

export const useDashboard = () => {
  const dashboard = inject(IKDashboard);
  if (!dashboard) throw new Error('Dashboard not provided');
  return dashboard;
};
